import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Loader2, RefreshCw, AlertTriangle, Target, Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useOrg } from '@/contexts/OrgContext'
import { useCadence } from '@/contexts/CadenceContext'
import { supabase } from '@/integrations/supabase/client'
import { TouchAngleEditor, type TouchAngleRow } from '@/components/qa/TouchAngleEditor'
import { stepGeneratesMessage } from '@/lib/cadence-step-predicates'

function toStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.filter((x): x is string => typeof x === 'string')
}

interface RawAngleRow extends TouchAngleRow {
  cadence_id: string | null
}

// Angle cards follow stepGeneratesMessage (shared helper): EVERY message-
// generating step gets an angle card, INCLUDING linkedin_comment. A plain Day-0
// invite (send_note !== true) generates no message and so gets no angle card.

// ────────────────────────────────────────────────────────────────────────────
// AnglesSection — the "Ángulos" tab. Reads the touch_angles rows scoped to THIS
// cadence (with the org-global cadence_id IS NULL fallback), then shows ONE card
// per REAL message-step day_offset of this cadence — never the fixed 1/3/5/7/9
// pattern. If a message-step day has no angle row yet, a synthetic empty card is
// surfaced so the user can define it.
// ────────────────────────────────────────────────────────────────────────────
export function AnglesSection({ cadenceId }: { cadenceId: string }) {
  const { orgId } = useOrg()
  const { cadences } = useCadence()

  const cadence = cadences.find((c) => c.id === cadenceId) ?? null

  // Real day_offsets of the message steps of THIS cadence (deduped, sorted).
  const messageDays = useMemo(() => {
    const days = new Set<number>()
    for (const s of cadence?.steps ?? []) {
      if (stepGeneratesMessage(s)) days.add(s.day_offset)
    }
    return [...days].sort((a, b) => a - b)
  }, [cadence])

  // Social-only cadence: HAS steps, but NONE of them generate an AI message
  // (only plain linkedin_connect / whatsapp / cold_call / tasks). These have NO
  // message days, so there are no per-touch angles to configure. We must NOT fall
  // back to the org-global effectiveByDay.keys() rows — that would surface
  // INHERITED angle cards as if they applied to this social-only cadence, which is
  // misleading. Instead we render an explicit empty-state (below). The fallback to
  // effectiveByDay.keys() is reserved ONLY for a still-empty cadence (no steps yet,
  // in construction), where showing the org-global template angles is a sensible seed.
  const stepCount = cadence?.steps?.length ?? 0
  const socialOnly = stepCount > 0 && messageDays.length === 0

  // ── Shared-angle detection (transparency only) ───────────────────────────────
  // touch_angles is scoped by (cadence, day) WITHOUT step_type — so when a single
  // day has ≥2 DISTINCT message step_types (e.g. Day 3 = {send_email,
  // linkedin_comment}), all of them read the SAME angle row and the UI draws ONE
  // card for that day. That's the phase-1 design (angle-per-day; angle-per-step is
  // roadmap), but it must be TRANSPARENT. Per day, collect the set of distinct
  // message step_types so we can surface an amber notice for any day with ≥2.
  const messageTypesByDay = useMemo(() => {
    const byDay = new Map<number, Set<string>>()
    for (const s of cadence?.steps ?? []) {
      if (!stepGeneratesMessage(s)) continue
      const set = byDay.get(s.day_offset) ?? new Set<string>()
      set.add(s.step_type)
      byDay.set(s.day_offset, set)
    }
    return byDay
  }, [cadence])

  // Days that share one angle row across ≥2 distinct message step_types.
  const sharedAngleDays = useMemo(
    () =>
      [...messageTypesByDay.entries()]
        .filter(([, types]) => types.size >= 2)
        .map(([day, types]) => ({ day, count: types.size }))
        .sort((a, b) => a.day - b.day),
    [messageTypesByDay],
  )

  // ── Read the angle rows for this cadence + the org-global fallback rows ──────
  const anglesQuery = useQuery({
    queryKey: ['touch-angles', orgId, cadenceId],
    enabled: !!orgId && !!cadenceId,
    queryFn: async (): Promise<RawAngleRow[]> => {
      const { data, error } = await supabase
        .from('touch_angles')
        .select('id, org_id, cadence_id, day_offset, label, capability_keywords, forbidden_capabilities, primary_numbers, primary_peers, hook_pattern, cta_pattern')
        .eq('org_id', orgId)
        .or(`cadence_id.eq.${cadenceId},cadence_id.is.null`)
        .order('day_offset', { ascending: true })
      if (error) throw new Error(error.message)
      return (data ?? []).map((r) => ({
        id: r.id as string,
        org_id: r.org_id as string,
        cadence_id: (r.cadence_id as string | null) ?? null,
        day_offset: Number(r.day_offset),
        label: (r.label as string) ?? '',
        capability_keywords: toStringArray(r.capability_keywords),
        forbidden_capabilities: toStringArray(r.forbidden_capabilities),
        primary_numbers: toStringArray(r.primary_numbers),
        primary_peers: toStringArray(r.primary_peers),
        hook_pattern: (r.hook_pattern as string | null) ?? null,
        cta_pattern: (r.cta_pattern as string | null) ?? null,
      }))
    },
  })

  // Effective angle per day: cadence-scoped row wins over the org-global one.
  const effectiveByDay = useMemo(() => {
    const byDay = new Map<number, RawAngleRow>()
    for (const r of anglesQuery.data ?? []) {
      if (r.cadence_id !== null && r.cadence_id !== cadenceId) continue
      const existing = byDay.get(r.day_offset)
      if (!existing || (existing.cadence_id === null && r.cadence_id === cadenceId)) {
        byDay.set(r.day_offset, r)
      }
    }
    return byDay
  }, [anglesQuery.data, cadenceId])

  // Build one row per REAL message-step day. If a day has no angle yet, emit a
  // synthetic empty row so the editor renders a card the user can fill.
  const rows: TouchAngleRow[] = useMemo(() => {
    // daysToShow priority:
    //  1) real message days of this cadence (the normal case),
    //  2) social-only (steps but no message days) → EMPTY: no per-touch angles
    //     exist to configure, and falling back to org-global rows would falsely
    //     present inherited angles as if they applied here,
    //  3) still-empty cadence (no steps yet) → seed from the org-global template
    //     angles (effectiveByDay) so the user has a starting point to tailor.
    const daysToShow = messageDays.length > 0
      ? messageDays
      : socialOnly
        ? []
        : [...effectiveByDay.keys()].sort((a, b) => a - b)
    return daysToShow.map((day) => {
      const found = effectiveByDay.get(day)
      if (found) {
        return {
          id: found.id,
          org_id: found.org_id,
          cadence_id: found.cadence_id ?? null,
          day_offset: found.day_offset,
          label: found.label,
          capability_keywords: found.capability_keywords,
          forbidden_capabilities: found.forbidden_capabilities,
          primary_numbers: found.primary_numbers,
          primary_peers: found.primary_peers,
          hook_pattern: found.hook_pattern,
          cta_pattern: found.cta_pattern,
        }
      }
      return {
        id: `empty-${day}`,
        org_id: orgId ?? '',
        day_offset: day,
        label: '',
        capability_keywords: [],
        forbidden_capabilities: [],
        primary_numbers: [],
        primary_peers: [],
        hook_pattern: null,
        cta_pattern: null,
      }
    })
  }, [messageDays, effectiveByDay, orgId, socialOnly])

  const usesFallback = rows.some((r) => effectiveByDay.get(r.day_offset)?.cadence_id === null)

  // ── Angle-collision detection (transparency only, never blocks saving) ───────
  // If two different touches (days) reuse the SAME capability_keyword (value-prop),
  // the pitch repeats between days — which is exactly what "ángulos por touch"
  // exists to prevent. Group by normalized keyword (lowercase/trim); flag any
  // keyword that appears in ≥2 distinct days.
  const angleCollisions = useMemo(() => {
    const daysByKeyword = new Map<string, { label: string; days: Set<number> }>()
    for (const r of rows) {
      for (const kw of r.capability_keywords) {
        const norm = kw.trim().toLowerCase()
        if (!norm) continue
        const entry = daysByKeyword.get(norm)
        if (entry) {
          entry.days.add(r.day_offset)
        } else {
          daysByKeyword.set(norm, { label: kw.trim(), days: new Set([r.day_offset]) })
        }
      }
    }
    return [...daysByKeyword.values()]
      .filter((e) => e.days.size >= 2)
      .map((e) => ({ label: e.label, days: [...e.days].sort((a, b) => a - b) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'))
  }, [rows])

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Cada touch de la cadencia debe cubrir una propuesta de valor DISTINTA de Yuno: qué
          value-prop usar, cuáles evitar (porque son de otro día), qué número citar y qué peer
          verificado mencionar. Las tarjetas siguen los días REALES de los pasos de mensaje de
          esta cadencia.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => anglesQuery.refetch()}
          disabled={anglesQuery.isFetching}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${anglesQuery.isFetching ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      <Card className="border-muted">
        <CardContent className="py-3 flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Las <strong>capacidades a usar</strong> son las value-props permitidas para este touch; las
            <strong> capacidades a evitar</strong> son las que pertenecen a otros días (para no repetir el
            pitch). Si una fila se borra de la base, la generación no falla: cae a un ángulo{' '}
            <strong>genérico por defecto</strong> del código. Ese fallback es genérico, no el ángulo
            específico de ese día — la generación sigue corriendo, pero el touch pierde su ángulo
            propio (deja de diferenciarse del resto).
          </span>
        </CardContent>
      </Card>

      {anglesQuery.isLoading ? (
        <Card><CardContent className="py-12 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando ángulos por touch…
        </CardContent></Card>
      ) : anglesQuery.error ? (
        <Card className="border-red-200 dark:border-red-900"><CardContent className="py-8 text-center text-sm">
          <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> No se pudieron leer los ángulos por touch.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => anglesQuery.refetch()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reintentar
          </Button>
        </CardContent></Card>
      ) : rows.length === 0 ? (
        socialOnly ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Esta cadencia no tiene pasos que generen mensajes con IA, así que no hay
            ángulos por touch que configurar.
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Esta cadencia no tiene pasos de mensaje todavía. Agrega pasos (email, DM, invitación…)
            en el tab Steps para poder definir su ángulo por touch.
          </div>
        )
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-3 text-sm font-medium text-muted-foreground">
            <Target className="h-4 w-4" />
            {rows.length} touch{rows.length === 1 ? '' : 'es'} de mensaje en esta cadencia
          </div>
          {usesFallback && (
            <div className="mb-3 rounded-md border border-muted bg-muted/30 p-3 flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Algunos ángulos son <strong>heredados de la configuración org-global</strong> (legacy,
                {' '}<code>cadence_id</code> nulo) porque esta cadencia aún no tiene ángulos propios para
                esos días. Es el comportamiento efectivo que el motor resuelve hoy.
              </span>
            </div>
          )}
          {sharedAngleDays.length > 0 && (
            <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="font-medium">
                  Ángulo compartido entre pasos del mismo día.
                </p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {sharedAngleDays.map(({ day, count }) => (
                    <li key={day}>
                      El <strong>día {day}</strong> tiene {count} pasos de mensaje (p.ej. email +
                      comentario) que comparten este mismo ángulo. Para diferenciarlos hará falta
                      ángulo por paso (roadmap).
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {angleCollisions.length > 0 && (
            <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="font-medium">
                  Ángulos repetidos entre touches — el pitch podría repetirse entre días.
                </p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {angleCollisions.map((c) => (
                    <li key={c.label}>
                      La capacidad <strong>&ldquo;{c.label}&rdquo;</strong> se repite en los días{' '}
                      {c.days.length === 2
                        ? c.days.join(' y ')
                        : `${c.days.slice(0, -1).join(', ')} y ${c.days[c.days.length - 1]}`}{' '}
                      — considera diferenciar el ángulo.
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {orgId && (
            <TouchAngleEditor rows={rows} orgId={orgId} cadenceId={cadenceId} />
          )}
        </div>
      )}
    </div>
  )
}
